import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * Options for configuring the behavior of the `ComponentEntityService`.
 */
export interface ComponentServiceOptions {
  /**
   * Whether to fetch the full entity details.
   * If `true`, retrieves the complete entity; otherwise, only selected fields.
   */
  fullEntity?: boolean;
}

/**
 * Interface defining methods for interacting with component entities.
 */
export interface IComponentService {
  /**
   * Retrieves components owned by the specified entity.
   *
   * @param entity - The entity for which components are being fetched.
   * @param options - Optional configuration for the service behavior.
   * @returns A promise that resolves to an array of component entities.
   */
  byOwner: (
    entity?: Entity,
    options?: ComponentServiceOptions,
  ) => Promise<Entity[]>;
}

/**
 * Default fields to fetch when querying for components.
 */
const componentFields = [
  'metadata.name',
  'metadata.uid',
  'metadata.namespace',
  'metadata.description',
  'metadata.title',
  'metadata.annotations',
];

/**
 * Service class for interacting with component entities in the catalog.
 *
 * @category CatalogServices
 */
export class ComponentEntityService implements IComponentService {
  private catalogApi: CatalogApi;

  /**
   * Creates an instance of the ComponentEntityService.
   *
   * @param catalogApi - An instance of the Catalog API for querying entities.
   */
  constructor(catalogApi: CatalogApi) {
    this.catalogApi = catalogApi;
  }

  /**
   * Fetches components owned by a specified entity.
   *
   * @param entity - The entity for which to fetch owned components.
   * @param options - Options to configure the fields or detail level of the response.
   * @returns A promise resolving to an array of owned component entities.
   *
   * @example
   * ```ts
   * import { useComponentEntity } from '@dewise-devex/backstage-plugin-utilities-common-client';
   * const components = await componentService.byOwner(productEntity);
   * console.log(components);
   * ```
   */
  async byOwner(entity?: Entity, options?: ComponentServiceOptions) {
    if (!entity) return [];

    const components = await this.catalogApi.getEntities({
      filter: [
        {
          kind: 'Component',
          'relations.ownedBy': `product:${entity.metadata.namespace}/${entity.metadata.name}`,
        },
      ],
      fields: options?.fullEntity ? undefined : componentFields,
    });
    return components.items || [];
  }
}

/**
 * React hook for creating an instance of the `ComponentEntityService`.
 *
 * @returns {IComponentService} An instance of `ComponentEntityService` bound to the catalog API.
 *
 * @category CatalogServices
 */
export const useComponentEntity = (): IComponentService => {
  return new ComponentEntityService(useApi(catalogApiRef));
};
